import { Empty, Icon, cx } from "../../../components/ui";
import { NAMA_RUBRIK, namaBulan } from "../logika";

// Progres raport cabang (team leader): angka ringkas, yang perlu ditindaklanjuti, lalu kartu
// per kelas dengan bilah lengkap per bulan. Klik kartu → panel rincian kelas.

const persen = (n, dari) => (dari ? Math.round((n / dari) * 100) : 0);

function Angka({ label, nilai, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-white">{nilai}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function Perhatian({ perhatian, onBukaKelas, onPilihKelasUtama }) {
  const { tanpaPemegang, menungguKelasUtama, tanpaProgram } = perhatian;
  if (!tanpaPemegang.length && !menungguKelasUtama.length && !tanpaProgram.length) return null;
  return (
    <section aria-label="Perlu ditindaklanjuti" className="anim-rise rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4">
      <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-amber-700 dark:text-amber-300">
        <Icon name="priority_high" className="text-base" />
        Perlu ditindaklanjuti
      </h2>

      {tanpaPemegang.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
            Kelas dengan 2+ trainer — tetapkan siapa yang mengisi raport
          </p>
          <ul className="mt-2 space-y-2">
            {tanpaPemegang.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-3 text-sm dark:bg-slate-900">
                <span>
                  <b>{k.nama}</b> <span className="text-slate-500">· {k.sesi} · {k.trainer.map((t) => t.nama).join(", ")}</span>
                </span>
                <button
                  type="button"
                  onClick={() => onBukaKelas(k.id)}
                  className="lift-press rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white"
                >
                  Tetapkan
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {menungguKelasUtama.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
            Murid di dua kelas raport — pilih kelas utamanya (yang mengisi raportnya)
          </p>
          <ul className="mt-2 space-y-2">
            {menungguKelasUtama.map((m) => (
              <li key={`${m.studentId}|${m.rubrik}`} className="rounded-xl bg-white p-3 text-sm dark:bg-slate-900">
                <p>
                  <b>{m.nama}</b> <span className="text-slate-500">· {m.studentId} · {NAMA_RUBRIK[m.rubrik]}</span>
                </p>
                <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Kelas utama ${m.nama}`}>
                  {m.kandidat.map((c) => (
                    <button
                      key={c.classId}
                      type="button"
                      onClick={() => onPilihKelasUtama(m.studentId, c.classId)}
                      className="lift-press rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-200"
                    >
                      {c.nama}{c.sesi ? ` (${c.sesi})` : ""}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tanpaProgram.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
            Kelas tanpa program — belum bisa diraport. Programnya diisi admin di Manajemen Kelas.
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {tanpaProgram.map((k) => `${k.nama}${k.sesi ? ` (${k.sesi})` : ""}`).join(" · ")}
          </p>
        </div>
      )}
    </section>
  );
}

function KartuKelas({ k, term, urutan, onBuka }) {
  const p = k.progres;
  return (
    <button
      type="button"
      onClick={onBuka}
      className={cx(
        "anim-rise lift-press flex w-full flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm hover:border-primary/50 dark:border-slate-800 dark:bg-slate-900",
        urutan < 4 && `anim-d${urutan + 1}`
      )}
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate text-base font-black text-slate-900 dark:text-white">{k.nama}</span>
          <span className="block text-xs text-slate-500">{[k.program, k.level, k.sesi].filter(Boolean).join(" · ")}</span>
        </span>
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-black text-primary dark:text-[#3987e5]">
          {NAMA_RUBRIK[k.rubrik]}
        </span>
      </span>
      <span className="mt-2 block text-xs">
        {k.pemegang.status === "belum-ditetapkan" ? (
          <span className="font-bold text-amber-700 dark:text-amber-300">Penanggung jawab belum ditetapkan</span>
        ) : k.pemegang.status === "tanpa-trainer" ? (
          <span className="font-bold text-amber-700 dark:text-amber-300">Belum ada trainer</span>
        ) : (
          <span className="text-slate-500">Diisi <b className="text-slate-700 dark:text-slate-200">{k.pemegang.nama}</b></span>
        )}
      </span>
      {p && (
        <>
          <span className="mt-3 grid grid-cols-3 gap-2">
            {p.perBulan.map((n, i) => (
              <span key={i} className="block">
                <span className="block text-[11px] font-bold text-slate-500">Bulan {i + 1} ({namaBulan(term.bulan[i])})</span>
                <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" aria-hidden="true">
                  <span className="block h-full rounded-full bg-green-500" style={{ width: `${persen(n, p.murid)}%` }} />
                </span>
                <span className="block text-[11px] tabular-nums text-slate-400">{n}/{p.murid} lengkap</span>
              </span>
            ))}
          </span>
          <span className="mt-3 block text-sm text-slate-600 dark:text-slate-300">
            <b>{p.terbit}</b> dari <b>{p.murid}</b> murid sudah terbit
          </span>
          <span className="block text-xs text-slate-400">
            {p.siapTerbit} siap terbit
            {p.berubah ? ` · ${p.berubah} berubah sejak terbit` : ""}
            {p.menungguKelasUtama ? ` · ${p.menungguKelasUtama} menunggu kelas utama` : ""}
          </span>
        </>
      )}
    </button>
  );
}

export default function ProgresTL({ data, onBukaKelas, onPilihKelasUtama }) {
  const { ringkas, perhatian, kelas, term } = data;
  const kartu = kelas.filter((k) => k.rubrik);
  return (
    <div className="space-y-6">
      <div className="anim-rise grid grid-cols-2 gap-3 md:grid-cols-4">
        <Angka label="Kelas raport" nilai={ringkas.kelas} />
        <Angka label="Murid" nilai={ringkas.murid} />
        <Angka label="Lengkap 3 bulan" nilai={ringkas.lengkap} />
        <Angka label="Sudah terbit" nilai={ringkas.terbit} sub={`${ringkas.siapTerbit} siap terbit`} />
      </div>
      <Perhatian perhatian={perhatian} onBukaKelas={onBukaKelas} onPilihKelasUtama={onPilihKelasUtama} />
      {!kartu.length ? (
        <Empty label="Belum ada kelas yang memakai raport di cabang ini." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {kartu.map((k, i) => (
            <KartuKelas key={k.id} k={k} term={term} urutan={i} onBuka={() => onBukaKelas(k.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
